import { DEFAULT_NOTIFICATION_DURATION_MS } from './config'
import type { NotificationItem, ShowNotificationOptions } from './types'

type Listener = () => void

/**
 * Стор стека уведомлений — обычный класс, не React-контекст: показать уведомление можно
 * из любого места (виджет, платформенный адаптер, обработчик ошибки запроса), не только
 * изнутри компонента. Архитектура — как у NotificationsStore эталонного ui-kit
 * (ConfigProvider/notifications/store.ts): список + подписчики + автоистечение по таймеру.
 * Визуальный рендер стека — NotificationList, он подписывается через useNotifications().
 */
class NotificationsStore {
  private listeners: Listener[] = []
  private items: NotificationItem[] = []
  private timers = new Map<string, ReturnType<typeof setTimeout>>()
  private lastId = 0

  private emit = (): void => {
    for (const listener of this.listeners) listener()
  }

  /** Таймер автозакрытия. Пересоздаётся, если то же уведомление показали ещё раз. */
  private scheduleExpire = (id: string, durationMs: number): void => {
    const running = this.timers.get(id)
    if (running) clearTimeout(running)

    this.timers.set(
      id,
      setTimeout(() => this.expire(id), durationMs),
    )
  }

  private setItems = (items: NotificationItem[]): void => {
    this.items = items
    this.emit()
  }

  subscribe = (listener: Listener): (() => void) => {
    this.listeners = [...this.listeners, listener]
    return () => {
      this.listeners = this.listeners.filter((existing) => existing !== listener)
    }
  }

  getItems = (): NotificationItem[] => this.items

  /** Помечает уведомление истёкшим — запускает анимацию ухода в NotificationList. */
  expire = (id: string): void => {
    this.setItems(this.items.map((item) => (item.id === id ? { ...item, isLeaving: true } : item)))
  }

  /** Убирает уведомление из стека — вызывается после анимации ухода. */
  remove = (id: string): void => {
    const running = this.timers.get(id)
    if (running) clearTimeout(running)
    this.timers.delete(id)

    this.setItems(this.items.filter((item) => item.id !== id))
  }

  show = (options: ShowNotificationOptions): string => {
    const theme = options.theme ?? 'INFO'
    const durationMs = options.durationMs ?? DEFAULT_NOTIFICATION_DURATION_MS

    // Одна и та же строка подряд — это не несколько событий, а одно, повторённое: три быстрых
    // тапа по тумблеру при мёртвой сети дают три одинаковых ошибки. Стопка одинаковых плашек
    // ничего не добавляет к смыслу, поэтому продлеваем показ той, что уже на экране.
    const duplicate = this.items.find(
      (item) => !item.isLeaving && item.text === options.text && item.theme === theme,
    )

    if (duplicate) {
      this.scheduleExpire(duplicate.id, duplicate.durationMs)
      return duplicate.id
    }

    this.lastId += 1
    const id = `notification-${this.lastId}`

    this.setItems([
      ...this.items,
      { id, theme, title: options.title, text: options.text, durationMs, isLeaving: false },
    ])

    this.scheduleExpire(id, durationMs)

    return id
  }

  success = (text: string, options?: Omit<ShowNotificationOptions, 'theme' | 'text'>): string =>
    this.show({ ...options, text, theme: 'SUCCESS' })

  error = (text: string, options?: Omit<ShowNotificationOptions, 'theme' | 'text'>): string =>
    this.show({ ...options, text, theme: 'ERROR' })

  warning = (text: string, options?: Omit<ShowNotificationOptions, 'theme' | 'text'>): string =>
    this.show({ ...options, text, theme: 'WARNING' })

  info = (text: string, options?: Omit<ShowNotificationOptions, 'theme' | 'text'>): string =>
    this.show({ ...options, text, theme: 'INFO' })
}

/** Единственный экземпляр стора — императивный вызов доступен из любого места приложения. */
export const notifications = new NotificationsStore()
